/**
 * Template mode for the campaign wizard (card S3): put a library sequence on
 * the campaign's steps, let the user fill the [slots] once, and check that
 * nothing unfinished can be saved. Pure functions, no I/O.
 *
 * Sending the text as written is the campaigns worker's job (card S1); the
 * wizard only saves it on each step as `template`.
 */

import type { SequenceStep } from '../types'
import { LEAD_PLACEHOLDERS } from '../variable-textarea'
import { DEFAULT_FALLBACK_HOURS, type MessageWriter } from '@/features/settings/mcp-helpers'
import { TEMPLATE_ANGLES, type TemplateAngle, type TemplateAngleId, type TemplateMessageKey } from './library'

const FOLLOW_UP_RE = /^follow_up_(\d+)$/
const SLOT_RE = /\[([^[\]\n]{1,80})\]/g
const VARIABLE_RE = /\{[^{}\n]{1,40}\}/g

/** Library messages for the follow-ups before the break-up, in order. */
const MIDDLE_KEYS: readonly TemplateMessageKey[] = ['fu1', 'fu2', 'pro_fu3', 'pro_fu4']

/** "None": no library sequence, the user writes every message (owner request 2026-10-20). */
export const NO_TEMPLATE = 'none'

/** What the template step can have picked: a library sequence or None. */
export type TemplateChoice = TemplateAngleId | typeof NO_TEMPLATE

/**
 * The user's own details, written once per campaign like any [slot]. Offered
 * as chips beside the lead's {variables}, so "{first_name}" (the lead) and
 * "[your first name]" (the sender) are told apart (owner request 2026-10-20).
 */
export const SENDER_SLOTS = ['your first name', 'your company', 'your title'] as const

export function findAngle(id: TemplateChoice | null | undefined): TemplateAngle | undefined {
  return TEMPLATE_ANGLES.find((a) => a.id === id)
}

function isFollowUp(step: Pick<SequenceStep, 'type'>): boolean {
  return FOLLOW_UP_RE.test(step.type)
}

/**
 * True for a step that sends a message (the first DM or any follow-up). In
 * template mode every one of them needs text; the connection request does not.
 */
export function isTemplateStep(step: Pick<SequenceStep, 'type'>): boolean {
  return step.type === 'first_dm' || isFollowUp(step)
}

/**
 * Which library message each step sends, by position. The break-up always
 * goes on the last follow-up, whatever the plan allows, so it is sent even
 * when the user keeps only one or two follow-ups. The follow-ups before it
 * take fu1, fu2, then Pro's pro_fu3 and pro_fu4. A follow-up past those (a
 * Custom sequence) has no library message and is left for the user to write.
 */
export function stepMessageKeys(steps: readonly Pick<SequenceStep, 'type'>[]): (TemplateMessageKey | undefined)[] {
  const followUps = steps.filter(isFollowUp).length
  let seen = 0
  return steps.map((step) => {
    if (step.type === 'first_dm') return 'first_dm'
    if (!isFollowUp(step)) return undefined
    seen += 1
    return seen === followUps ? 'breakup' : MIDDLE_KEYS[seen - 1]
  })
}

/** "First DM", "Follow-up 2", with the step type as the fallback. */
export function stepLabel(step: Pick<SequenceStep, 'type'>): string {
  if (step.type === 'first_dm') return 'First DM'
  const n = FOLLOW_UP_RE.exec(step.type)?.[1]
  return n ? `Follow-up ${n}` : step.type
}

/**
 * Put an angle's messages on the steps. Message steps get the library text,
 * or none where the library has no message for that position; the connection
 * request is left as it is. Replaces earlier edits, so the caller confirms
 * before switching.
 */
export function applyAngle(steps: readonly SequenceStep[], angleId: TemplateAngleId): SequenceStep[] {
  const angle = findAngle(angleId)
  if (!angle) return [...steps]
  const keys = stepMessageKeys(steps)
  return steps.map((step, i) => {
    if (!isTemplateStep(step)) return step
    const key = keys[i]
    return withTemplate(step, key ? angle.messages[key] : undefined)
  })
}

/**
 * After the steps change (one added, removed or retyped), move the library
 * messages to the positions they now belong to. Text the user edited stays
 * where it is; only text still identical to a library message moves, so the
 * break-up follows the last follow-up.
 */
export function syncAngle(steps: readonly SequenceStep[], angleId: TemplateChoice): SequenceStep[] {
  const angle = findAngle(angleId)
  if (!angle) return [...steps]
  const library = new Set<string>(Object.values(angle.messages))
  const keys = stepMessageKeys(steps)
  return steps.map((step, i) => {
    if (!isTemplateStep(step)) return step
    if (step.template !== undefined && !library.has(step.template)) return step
    const key = keys[i]
    return withTemplate(step, key ? angle.messages[key] : undefined)
  })
}

function withTemplate(step: SequenceStep, template: string | undefined): SequenceStep {
  if (template !== undefined) return { ...step, template }
  if (step.template === undefined) return step
  const { template: _removed, ...rest } = step
  return rest
}

/** The message steps emptied for None: the user writes each one. The connection request is left as it is. */
export function clearMessageSteps(steps: readonly SequenceStep[]): SequenceStep[] {
  return steps.map((step) => (isTemplateStep(step) ? withTemplate(step, undefined) : step))
}

/** The steps with every `template` removed (back to AI writing). */
export function clearTemplates(steps: readonly SequenceStep[]): SequenceStep[] {
  return steps.map((step) => {
    if (step.template === undefined) return step
    const { template: _removed, ...rest } = step
    return rest
  })
}

/** Unique [slots] across the texts, in order of first appearance, without brackets. */
export function findSlots(texts: readonly (string | undefined)[]): string[] {
  const seen: string[] = []
  for (const text of texts) {
    for (const m of (text ?? '').matchAll(SLOT_RE)) {
      const name = m[1] ?? ''
      if (!seen.includes(name)) seen.push(name)
    }
  }
  return seen
}

/** Replace every [slot] that has a non-blank value; unfilled slots stay as they are. */
export function fillSlots(text: string, values: Readonly<Record<string, string>>): string {
  return text.replace(SLOT_RE, (whole, name: string) => {
    const value = values[name]?.trim()
    return value ? value : whole
  })
}

/**
 * The slot that holds the booking link. It is never typed: the send only
 * allows the campaign's own link (link safety in the campaigns worker), so
 * any other URL here would get every message held.
 */
export const BOOKING_LINK_SLOT = 'your booking link'

/**
 * The slot values with [your booking link] set to the campaign's own link
 * (Details step). A typed value for it is dropped; with no campaign link the
 * slot stays unfilled and templateProblems says what to do.
 */
export function slotValuesWithLink(
  values: Readonly<Record<string, string>>,
  campaignLink: string | null,
): Record<string, string> {
  const { [BOOKING_LINK_SLOT]: _typed, ...rest } = values
  const link = campaignLink?.trim() ?? ''
  return link ? { ...rest, [BOOKING_LINK_SLOT]: link } : rest
}

/**
 * The link as a whole-link pattern: "https://cal.com/a" does not match inside
 * "https://cal.com/ab", but a full stop or comma after it (end of a sentence)
 * still matches.
 */
function wholeLinkRe(link: string): RegExp {
  return new RegExp(`${link.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}(?![\\w/?#&=%~+-]|[.,:]\\w)`, 'g')
}

/** True when any step's template text contains this link (whole link only). */
export function templatesUseLink(steps: unknown, link: string | null | undefined): boolean {
  const l = link?.trim() ?? ''
  if (!l || !Array.isArray(steps)) return false
  return steps.some((s) => {
    const t = (s as { template?: unknown } | null)?.template
    return typeof t === 'string' && wholeLinkRe(l).test(t)
  })
}

/**
 * The stored steps with the campaign's old link swapped for the new one in
 * every template text (the link was filled in at creation). Null when there
 * is nothing to change: no template uses the old link, or there is no new
 * link to put in. Other step fields are kept as stored.
 */
export function retargetTemplateLink(
  steps: unknown,
  oldLink: string | null | undefined,
  newLink: string | null | undefined,
): Record<string, unknown>[] | null {
  const from = oldLink?.trim() ?? ''
  const to = newLink?.trim() ?? ''
  if (!to || from === to || !templatesUseLink(steps, from)) return null
  const re = wholeLinkRe(from)
  return (steps as Record<string, unknown>[]).map((s) =>
    typeof s?.['template'] === 'string' ? { ...s, template: (s['template'] as string).replace(re, () => to) } : s,
  )
}

/** {variables} in the text that the send does not know how to fill. */
export function unknownVariables(text: string): string[] {
  const known = new Set<string>(LEAD_PLACEHOLDERS)
  return [...new Set(text.match(VARIABLE_RE) ?? [])].filter((v) => !known.has(v))
}

/**
 * Everything that stops a template campaign from being saved, in plain words
 * for the wizard. Empty means ready. Every message step needs text (including
 * a follow-up the library has no message for), no [slot] may be left after
 * filling, and only known {variables} may appear.
 */
export function templateProblems(
  steps: readonly SequenceStep[],
  slotValues: Readonly<Record<string, string>>,
): string[] {
  const problems: string[] = []
  const unfilled = new Set<string>()
  for (const step of steps) {
    if (!isTemplateStep(step)) continue
    const label = stepLabel(step)
    const text = fillSlots(step.template ?? '', slotValues).trim()
    if (!text) {
      problems.push(`${label} has no message yet.`)
      continue
    }
    for (const slot of findSlots([text])) unfilled.add(slot)
    for (const v of unknownVariables(text)) {
      problems.push(`${label} uses ${v}, which is not a variable we can fill. Use one of the chips.`)
    }
  }
  // A typed value with square brackets would read as a new [slot] with no box
  // to fill it, and the send holds any [..] left in a message. Say so plainly.
  const ownSlots = findSlots(steps.filter(isTemplateStep).map((s) => s.template))
  for (const slot of ownSlots) {
    if (/[[\]]/.test(slotValues[slot] ?? '')) problems.push(`[${slot}] can't contain square brackets. Take them out.`)
  }
  for (const slot of unfilled) {
    // Only the message's own slots: anything else came from a bracketed value, reported above.
    if (!ownSlots.includes(slot)) continue
    problems.push(
      slot === BOOKING_LINK_SLOT
        ? `[${slot}] uses this campaign's booking link, and the campaign has none. Add one in Details, or take [${slot}] out of the messages.`
        : `Fill in [${slot}].`,
    )
  }
  return problems
}

/** Who writes the campaign's messages, as the wizard's Method step offers it. */
export type WizardMessageWriter = MessageWriter

/**
 * The steps and settings the wizard saves for the chosen writer. AI writing
 * drops any template text and leaves message_writer unset (the server's
 * default); template mode saves the filled text per step plus the writer and
 * the sequence picked (reporting only; None saves no sequence). Your Claude
 * ('mcp') drops template text and saves the writer plus the hand-back hours
 * (settings.mcp_fallback_hours, the same keys messageWriterPatch saves from
 * the campaign page).
 */
export function messageWriterPayload(
  writer: WizardMessageWriter,
  steps: readonly SequenceStep[],
  angle: TemplateChoice | null,
  slotValues: Readonly<Record<string, string>>,
  fallbackHours: number = DEFAULT_FALLBACK_HOURS,
): {
  readonly steps: SequenceStep[]
  readonly settings: {
    readonly message_writer?: 'template' | 'mcp'
    readonly template_angle?: TemplateAngleId
    readonly mcp_fallback_hours?: number
  }
} {
  if (writer === 'mcp') {
    return { steps: clearTemplates(steps), settings: { message_writer: 'mcp', mcp_fallback_hours: fallbackHours } }
  }
  if (writer !== 'template') return { steps: clearTemplates(steps), settings: {} }
  return {
    steps: finalTemplateSteps(steps, slotValues),
    settings: { message_writer: 'template', ...(angle && angle !== NO_TEMPLATE ? { template_angle: angle } : {}) },
  }
}

/** The steps as they are saved: slots filled, text trimmed. Steps without a template are unchanged. */
export function finalTemplateSteps(
  steps: readonly SequenceStep[],
  slotValues: Readonly<Record<string, string>>,
): SequenceStep[] {
  return steps.map((step) =>
    isTemplateStep(step) && step.template !== undefined
      ? { ...step, template: fillSlots(step.template, slotValues).trim() }
      : step,
  )
}
